import React, { useEffect, useState } from "react";
import { Outlet, Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Mail, Phone, MapPin } from "lucide-react";
import logo from "../images/logo.png";

export default function SiteLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navClass = ({ isActive }) =>
    `transition hover:text-cyan-300 ${
      isActive ? "text-cyan-300" : "text-slate-300"
    }`;

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-950 text-white">
      <style>{`
        html { scroll-behavior: smooth; }

        @keyframes waveFlow {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        @keyframes floatCard {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }

        .wave-track {
          width: 200%;
          animation: waveFlow 20s linear infinite;
        }

        .floating-card {
          animation: floatCard 6s ease-in-out infinite;
        }
      `}</style>

      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.18),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.12),transparent_30%),linear-gradient(to_bottom,#020617,#08111f,#020617)]" />
        <div className="absolute -left-24 top-10 h-80 w-80 rounded-full bg-cyan-400/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[28rem] w-[28rem] rounded-full bg-emerald-400/10 blur-3xl" />
      </div>

      <header
        className={`sticky top-0 z-50 border-b border-white/10 backdrop-blur-xl transition-all duration-300 ${
          isScrolled ? "bg-slate-950/90" : "bg-slate-950/65"
        }`}
      >
        <div
          className={`mx-auto flex max-w-7xl items-center justify-between px-6 transition-all duration-300 ${
            isScrolled ? "py-3" : "py-4"
          }`}
        >
          <Link to="/" className="flex items-center gap-3">
            <img
              src={logo}
              alt="AquaHome Logo"
              className={`w-auto object-contain transition-all duration-300 ${
                isScrolled ? "h-9" : "h-11"
              }`}
            />
            <div>
              <h1 className="text-lg font-bold tracking-wide">AquaHome</h1>
              <p className="text-xs text-slate-400">
                Smart Water Treatment Infrastructure
              </p>
            </div>
          </Link>

          <nav className="hidden items-center gap-8 text-sm md:flex">
            <NavLink to="/" end className={navClass}>
              Home
            </NavLink>
            <NavLink to="/about" className={navClass}>
              About
            </NavLink>
            <NavLink to="/solutions" className={navClass}>
              Solutions
            </NavLink>
            <NavLink to="/gallery" className={navClass}>
              Gallery
            </NavLink>
            <NavLink to="/blog" className={navClass}>
              Blog
            </NavLink>
            <NavLink to="/contact" className={navClass}>
              Contact
            </NavLink>
          </nav>

          <div className="hidden md:block">
            <Link
              to="/contact"
              className="rounded-xl bg-cyan-500 px-5 py-2.5 font-semibold text-slate-950 transition hover:bg-cyan-400"
            >
              Get Proposal
            </Link>
          </div>

          <button
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white md:hidden"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
              className="border-t border-white/10 bg-slate-950/95 px-6 py-4 backdrop-blur-xl md:hidden"
            >
              <nav className="flex flex-col gap-3 text-sm text-slate-200">
                <NavLink to="/" end onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-2 py-2 hover:bg-white/5">
                  Home
                </NavLink>
                <NavLink to="/about" onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-2 py-2 hover:bg-white/5">
                  About
                </NavLink>
                <NavLink to="/solutions" onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-2 py-2 hover:bg-white/5">
                  Solutions
                </NavLink>
                <NavLink to="/gallery" onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-2 py-2 hover:bg-white/5">
                  Gallery
                </NavLink>
                <NavLink to="/blog" onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-2 py-2 hover:bg-white/5">
                  Blog
                </NavLink>
                <NavLink to="/contact" onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-2 py-2 hover:bg-white/5">
                  Contact
                </NavLink>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main>
        <Outlet />
      </main>

      <footer className="border-t border-white/10 bg-slate-950/80 pb-28 md:pb-0">
        <div className="mx-auto max-w-7xl px-6 py-12 text-slate-400">
          <div className="grid gap-10 md:grid-cols-3">
            <div>
              <h4 className="text-lg font-bold text-white">AquaHome</h4>
              <p className="mt-3 leading-7">
                Premium arsenic removal plants, iron removal plants, RO plants,
                UV systems and softener plants — removing arsenic, iron,
                bacteria and hardness for residential societies, mansions and
                real estate projects in Kolkata, Newtown and Rajarhat.
              </p>
            </div>

            <div>
              <h5 className="mb-3 font-semibold text-white">Quick Links</h5>
              <ul className="space-y-2">
                <li><Link to="/" className="hover:text-cyan-300">Home</Link></li>
                <li><Link to="/about" className="hover:text-cyan-300">About</Link></li>
                <li><Link to="/solutions" className="hover:text-cyan-300">Solutions</Link></li>
                <li><Link to="/gallery" className="hover:text-cyan-300">Gallery</Link></li>
                <li><Link to="/blog" className="hover:text-cyan-300">Blog</Link></li>
                <li><Link to="/contact" className="hover:text-cyan-300">Contact</Link></li>
              </ul>
            </div>

            <div>
              <h5 className="mb-3 font-semibold text-white">Contact</h5>
              <div className="space-y-3 leading-7">
                <p className="flex items-start gap-2">
                  <Mail size={18} className="mt-1 text-cyan-300" />
                  <span>info@hydroteclimited.com</span>
                </p>
                <p className="flex items-start gap-2">
                  <Phone size={18} className="mt-1 text-cyan-300" />
                  <span>+91 8697702088</span>
                </p>
                <p className="flex items-start gap-2">
                  <MapPin size={18} className="mt-1 text-cyan-300" />
                  <span>CC-100, Street-233, Newtown, Kolkata, West Bengal - 700156</span>
                </p>
              </div>
            </div>
          </div>

          <div className="mt-10 border-t border-white/10 pt-6 text-center text-sm text-slate-500">
            © 2026 AquaHome. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}